import React from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton/IconButtonWithBackground';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import EditIcon from '@wix/da-ds/pkg/Icons/24x24/Edit';
import TrashcanIcon from '@wix/da-ds/pkg/Icons/24x24/Trashcan';
import LockIcon from '@wix/da-ds/pkg/Icons/24x24/Lock';
import AddIcon from '@wix/da-ds/pkg/Icons/16x16/Add';
import GlobeIcon from '@wix/da-ds/pkg/Icons/24x24/Language';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { ShippingProfile, ShippingDestination } from '../../../../redux/types';

import s from './ProfileList.scss';

interface Props {
  profiles: ShippingProfile[];
  destinations: ShippingDestination[];
  onEditProfile: (profile: ShippingProfile) => void;
  onDeleteProfile: (profile: ShippingProfile) => void;
  onAddProfile?: () => void;
}

function formatRateSummary(profile: ShippingProfile, destinations: ShippingDestination[]): string {
  return profile.rates
    .map(rate => {
      const dest = destinations.find(
        d => d.destinationType === rate.destinationType && d.destinationId === rate.destinationId
      );
      const name = dest?.name ?? rate.destinationType ?? '';
      return `${name} $${rate.priceUsd}`;
    })
    .join(' · ');
}

const ProfileList: React.FC<Props> = ({
  profiles,
  destinations,
  onEditProfile,
  onDeleteProfile,
  onAddProfile,
}) => {
  const { t } = useTranslation();

  return (
    <div className={s['root']}>
      <ul className={s['list']}>
        {profiles.map(profile => (
          <li key={profile.shippingProfileid} className={s['item']}>
            <div className={s['item-details']}>
              <div className={s['item-icon']}>
                <GlobeIcon size={IconSize.MEDIUM} />
              </div>
              <div className={s['item-info']}>
                <span className={s['item-title']}>{profile.title}</span>
                <span className={s['item-summary']}>
                  {formatRateSummary(profile, destinations)}
                </span>
              </div>
            </div>
            <div className={s['item-actions']}>
              <IconButton
                size="medium"
                variant="main"
                icon={EditIcon}
                onClick={() => onEditProfile(profile)}
                aria-label={t('manageShippingProfiles.editProfile.ariaLabel')}
              />
              <div className={s['item-divider']} />
              {profile.isInUse ? (
                <SimpleTooltip text={t('manageShippingProfiles.profileInUse.tooltip')} width={220}>
                  <IconButton
                    size="medium"
                    variant="main"
                    icon={LockIcon}
                    disabled
                    aria-label={t('manageShippingProfiles.profileInUse.ariaLabel')}
                  />
                </SimpleTooltip>
              ) : (
                <IconButton
                  size="medium"
                  variant="main"
                  icon={TrashcanIcon}
                  onClick={() => onDeleteProfile(profile)}
                  aria-label={t('manageShippingProfiles.deleteProfile.ariaLabel')}
                />
              )}
            </div>
          </li>
        ))}
      </ul>
      {onAddProfile && (
        <TextButton size="medium" variant="main" icon={AddIcon} onClick={onAddProfile}>
          {t('manageShippingProfiles.addNewProfile')}
        </TextButton>
      )}
    </div>
  );
};
ProfileList.displayName = 'ProfileList';

export default ProfileList;
